import React from 'react';
import pickaxeArt from './tool-icons/pickaxe.png';
import hammerArt from './tool-icons/hammer.png';
import axeArt from './tool-icons/axe.png';
import bowArt from './tool-icons/bow.png';
import swordArt from './tool-icons/sword.png';
import shieldArt from './tool-icons/shield.png';

// Minimal stroke-style icon set for the professions menu - simple geometric
// glyphs (not licensed artwork), consistent 24x24 viewBox / currentColor so
// they inherit whatever gold/cream color the surrounding UI applies.
const base = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

// Real (colored) pixel-art tool icons, used only by iconFor's "Outils
// nécessaires" list below - kept separate from ICONS/PROFESSION_ICON so the
// category list and hero icon (which recolor via currentColor/drop-shadow)
// are unaffected. Tools with no good match in the source art keep the
// hand-drawn line icon via the ICONS map further down.
const TOOL_ART: Record<string, JSX.Element> = {
  pickaxe: <img src={pickaxeArt} alt="" />,
  hammer: <img src={hammerArt} alt="" />,
  axe: <img src={axeArt} alt="" />,
  bow: <img src={bowArt} alt="" />,
  sword: <img src={swordArt} alt="" />,
  shield: <img src={shieldArt} alt="" />,
};

export const ICONS: Record<string, JSX.Element> = {
  wheat: (
    <svg {...base}>
      <path d="M12 21V8" />
      <path d="M12 8c-2-1-3-3-3-5 2 1 3 3 3 5z" />
      <path d="M12 8c2-1 3-3 3-5-2 1-3 3-3 5z" />
      <path d="M12 13c-2-1-4-1-5-3 2 0 4 1 5 3z" />
      <path d="M12 13c2-1 4-1 5-3-2 0-4 1-5 3z" />
    </svg>
  ),
  pickaxe: (
    <svg {...base}>
      <path d="M3 10.5c3-6.5 15-6.5 18 0" />
      <path d="M3 10.5 1.5 13" />
      <path d="M21 10.5 22.5 13" />
      <path d="M12 8v2.5" />
      <path d="M12 10.5 18 21" />
    </svg>
  ),
  torch: (
    <svg {...base}><path d="M12 3c-2 2-2 4 0 5s2 3 0 5" /><path d="M12 13v8" /><path d="M9 21h6" /></svg>
  ),
  needle: (
    <svg {...base}><circle cx="17" cy="7" r="2" /><path d="M15.5 8.5 4 20" /><path d="M4 20l1.5-4.5L9 17z" /></svg>
  ),
  loom: (
    <svg {...base}><path d="M3 12c3-4 15-4 18 0-3 4-15 4-18 0Z" /><path d="M3 12h18" /></svg>
  ),
  hammer: (
    <svg {...base}><rect x="1.5" y="2.5" width="13" height="7" rx="1" transform="rotate(-35 8 6)" /><path d="M10.5 9.5 5 21" /></svg>
  ),
  anvil: (
    <svg {...base}><path d="M4 14h8l3 3h5v2H4z" /><path d="M9 19v2h6v-2" /><path d="M7 14V9h6l3 3" /></svg>
  ),
  flask: (
    <svg {...base}><path d="M10 3h4" /><path d="M11 3v6l-6 10a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-6-10V3" /><path d="M8 15h8" /></svg>
  ),
  mortar: (
    <svg {...base}><path d="M5 12a7 4 0 0 0 14 0" /><path d="M4 12h16" /><path d="M15 4 12 12" /></svg>
  ),
  bow: (
    <svg {...base}><path d="M6 3c8 3 8 15 0 18" /><path d="M6 3v18" /><path d="M6 12h12" /></svg>
  ),
  quiver: (
    <svg {...base}><path d="M9 21 8 9c0-3 1-6 4-6s4 3 4 6l-1 12" /><path d="M8 9h8" /></svg>
  ),
  sword: (
    <svg {...base}><path d="M12 2v14" /><path d="M8 6h8" /><path d="M12 16l-3 5" /><path d="M12 16l3 5" /></svg>
  ),
  shield: (
    <svg {...base}><path d="M12 3l7 3v6c0 5-3 7.5-7 9-4-1.5-7-4-7-9V6z" /></svg>
  ),
  pot: (
    <svg {...base}><path d="M5 10h14l-1.2 9a2 2 0 0 1-2 1.7H8.2a2 2 0 0 1-2-1.7z" /><path d="M3 10h18" /><path d="M9 10V8a3 3 0 0 1 6 0v2" /></svg>
  ),
  knife: (
    <svg {...base}><path d="M5 19 17 7" /><path d="M17 7c1-1 3-3 4-2s-1 3-2 4" /><path d="M5 19l-2 2" /></svg>
  ),
  axe: (
    <svg {...base}><path d="M13 3c4-0.5 7 2 7 5.5S17 13 13 12.5l-2-2.2 2-2.1z" /><path d="M11 10 4 21" /></svg>
  ),
  fishingRod: (
    <svg {...base}><path d="M3 21 19 5" /><path d="M19 5c2.5 2 2.5 6 0 10" /><circle cx="19" cy="15.5" r="1" /></svg>
  ),
  net: (
    <svg {...base}><path d="M4 4h16v16H4z" /><path d="M4 4l16 16" /><path d="M20 4 4 20" /><path d="M12 4v16" /><path d="M4 12h16" /></svg>
  ),
  star: (
    <svg {...base}><path d="M12 3l2.4 5.6 6 .5-4.6 4 1.4 6-5.2-3.2L6.8 19l1.4-6-4.6-4 6-.5z" /></svg>
  ),
};

// Keyword-matches a freeform tool/category name (accent-insensitive) to one
// of the icons above - decorative only, falls back to a generic star.
export function iconFor(name: string): JSX.Element {
  const key = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

  if (key.includes('pioche')) return TOOL_ART.pickaxe;
  if (key.includes('torche')) return ICONS.torch;
  if (key.includes('aiguille')) return ICONS.needle;
  if (key.includes('metier a tisser') || key.includes('tisser')) return ICONS.loom;
  if (key.includes('marteau')) return TOOL_ART.hammer;
  if (key.includes('enclume')) return ICONS.anvil;
  if (key.includes('alambic')) return ICONS.flask;
  if (key.includes('mortier')) return ICONS.mortar;
  if (key.includes('arc') && !key.includes('archer')) return TOOL_ART.bow;
  if (key.includes('carquois')) return ICONS.quiver;
  if (key.includes('epee')) return TOOL_ART.sword;
  if (key.includes('bouclier')) return TOOL_ART.shield;
  if (key.includes('marmite') || key.includes('broche')) return ICONS.pot;
  if (key.includes('couteau')) return ICONS.knife;
  if (key.includes('hache') || key.includes('scie')) return TOOL_ART.axe;
  if (key.includes('canne')) return ICONS.fishingRod;
  if (key.includes('filet')) return ICONS.net;
  return ICONS.star;
}

// Direct id -> icon mapping for the categories list (more reliable than
// keyword-matching a display name).
export const PROFESSION_ICON: Record<string, JSX.Element> = {
  miner: TOOL_ART.pickaxe,
  weaver: ICONS.needle,
  blacksmith: TOOL_ART.hammer,
  alchemist: ICONS.flask,
  cook: ICONS.pot,
  hunter: ICONS.knife,
  lumberjack: TOOL_ART.axe,
  farmer: ICONS.wheat,
  fisher: ICONS.fishingRod,
  archer: TOOL_ART.bow,
  warrior: TOOL_ART.sword,
};
